import PropTypes from 'prop-types';
import React from 'react';
import bindAll from 'lodash.bindall';
import {connect} from 'react-redux';
import {compose} from 'redux';
import {intlShape, injectIntl, defineMessages} from 'react-intl';
import VM from 'openblock-vm';

import HardwareConsoleComponent from '../components/hardware-console/hardware-console.jsx';

import { ESPLoader, Transport } from "../../lib";
import CryptoJS from 'crypto-js';

// Import binary files
import bootloaderBin from '../lib/bin_files/bootloader.bin';
import partitionsBin from '../lib/bin_files/partitions.bin';
import firmwareBin from '../lib/bin_files/firmware.bin';

import {
    openSerialportMenu,
    closeSerialportMenu,
    serialportMenuOpen
} from '../reducers/menus';

import {showAlertWithTimeout} from '../reducers/alerts';
import {setBaudrate, setEol, switchHexForm, switchAutoScroll, switchPause} from '../reducers/hardware-console';

import {getSharedSerialPort, getSharedPortBaudrate, setSharedSerialPort} from './hardware-header.jsx';
import {setScannedDevices} from '../lib/scanned-devices-store.js';

const messages = defineMessages({
    noLineTerminators: {
        defaultMessage: 'No line terminators',
        description: 'no line terminators in the end of serialsport messge to send',
        id: 'gui.hardwareConsole.noLineTerminators'
    },
    lineFeed: {
        defaultMessage: 'Line feed',
        description: 'Line feed in the end of serialsport messge to send',
        id: 'gui.hardwareConsole.lineFeed'
    },
    carriageReturn: {
        defaultMessage: 'Carriage return',
        description: 'Carriage return in the end of serialsport messge to send',
        id: 'gui.hardwareConsole.carriageReturn'
    },
    lfAndCr: {
        defaultMessage: 'LF & CR',
        description: 'LF & CR in the end of serialsport messge to send',
        id: 'gui.hardwareConsole.lfAndCr'
    }
});

const baudrateList = [
    {key: '1200', value: 1200},
    {key: '2400', value: 2400},
    {key: '4800', value: 4800},
    {key: '9600', value: 9600},
    {key: '14400', value: 14400},
    {key: '19200', value: 19200},
    {key: '38400', value: 38400},
    {key: '57600', value: 57600},
    {key: '76800', value: 76800},
    {key: '115200', value: 115200},
    {key: '256000', value: 256000}
];

const eolList = [
    {key: 'null', value: messages.noLineTerminators},
    {key: 'lf', value: messages.lineFeed},
    {key: 'cr', value: messages.carriageReturn},
    {key: 'lfAndCr', value: messages.lfAndCr}
];

const MAX_CONSOLE_LENGTH = 32768;

class HardwareConsole extends React.Component {
    constructor (props) {
        super(props);
        bindAll(this, [
            'handleClickClean',
            'handleClickPlay',
            'handleClickAutoScroll',
            'handleClickHexForm',
            'handleClickPause',
            'handleClickSend',
            'handleInputChange',
            'handleKeyPress',
            'handleKeyDown',
            'handleSelectBaudrate',
            'handleSelectEol',
            'onReciveData',
            'onWebSerialData',
            'writeToPeripheral',
            'handleClickScan',
            'ensurePortOpen',
            'pauseConsoleForUpload',
            'resumeConsoleAfterUpload',
            'updateSharedPort',
            '_handleDeviceDisconnect',
            'flashBinFilesToESP32',
            'requestUsbPort'
        ]);
        this.state = {
            consoleArray: new Uint8Array(0),
            dataToSend: '',
            isPlaying: false,
            isScanning: false,
            scannedDevices: [],
            scanTimeLeft: 10, // Thời gian còn lại khi scan
            isFlashing: false, // Trạng thái đang flash
            flashProgress: 0, // Tiến độ flash (%)
            portType: null, // 'com' or 'usb' - loại port đang sử dụng
            showBootPrompt: false // Hiển thị prompt nhấn nút BOOT
        };
        
        // Espressif USB VID và PIDs
        this.ESPRESSIF_VID = 0x303A;
        this.USB_JTAG_SERIAL_PID = 0x1001; // USB-JTAG-Serial
        this.USB_SERIAL_PID = 0x1002; // USB-Serial (CDC)
        this.USB_OTG_PID = 0x0002; // USB-OTG
        this._recivceBuffer = new Uint8Array(0);
        this._jsonBuffer = '';
        this._useWebSerial = false;
        this._port = null;
        this._reader = null;
        this._writer = null;
        this._isPortOpen = false;
        this._wasPlayingBeforeUpload = false; // Track Play state for restore
        this._scanTimeout = null; // Timeout cho auto-stop scan
        this._scanCountdownInterval = null; // Interval cho countdown
    }

    // Hàm mới: đảm bảo port được mở
    async ensurePortOpen () {
        if (this._isPortOpen && this._port && this._writer) {
            console.log('✓ Port already open, reusing connection');
            return true;
        }

        if (!('serial' in navigator)) {
            this.props.onNoPeripheralIsConnected();
            return false;
        }

        try {
            const sharedPort = getSharedSerialPort();
            const sharedBaudrate = getSharedPortBaudrate();

            let port;
            let needToOpen = true;

            if (sharedPort) {
                console.log('✓ Using shared port from previous operation');
                port = sharedPort;

                // ✅ Kiểm tra xem port có còn hợp lệ không
                if (port.readable && port.writable) {
                    console.log('✓ Port already open from shared source');
                    needToOpen = false;
                } else {
                    // Port không còn hợp lệ, yêu cầu chọn lại
                    console.warn('⚠ Shared port no longer valid, requesting new port...');
                    await this._handleDeviceDisconnect();
                    port = await navigator.serial.requestPort();
                    this.updateSharedPort(port, parseInt(this.props.baudrate, 10));
                }
            } else {
                console.log('No shared port, requesting new port...');
                port = await navigator.serial.requestPort();

                // ✅ QUAN TRỌNG: Cập nhật shared port để Upload có thể dùng
                this.updateSharedPort(port, parseInt(this.props.baudrate, 10));
            }

            if (needToOpen) {
                let retries = 3;
                let opened = false;

                while (retries > 0 && !opened) {
                    try {
                        const baudRate = sharedBaudrate || parseInt(this.props.baudrate, 10);
                        await port.open({baudRate: baudRate});
                        opened = true;
                        console.log(`✓ Port opened at ${baudRate} baud`);

                        // ✅ Cập nhật shared port sau khi mở thành công
                        this.updateSharedPort(port, baudRate);
                    } catch (openErr) {
                        retries--;
                        if (retries === 0) {
                            // ✅ Nếu không thể mở, có thể thiết bị đã bị ngắt
                            if (openErr.message && openErr.message.includes('disconnected')) {
                                console.warn('⚠ Device disconnected, please reconnect');
                                await this._handleDeviceDisconnect();
                            }
                            throw openErr;
                        }

                        console.log(`Port busy, retrying... (${retries} attempts left)`);
                        await new Promise(resolve => setTimeout(resolve, 1000));
                    }
                }
            }

            this._port = port;
            this._writer = port.writable.getWriter();
            this._isPortOpen = true;
            this._useWebSerial = true;

            console.log('✓ Port ready for communication');
            return true;
        } catch (err) {
            console.error('Failed to open port:', err);
            this.props.onNoPeripheralIsConnected();
            return false;
        }
    }

    // Hàm mới: Cập nhật shared port để Upload có thể sử dụng
    updateSharedPort(port, baudrate) {
        setSharedSerialPort(port, baudrate);
        console.log(`✓ Shared port updated for upload (baudrate: ${baudrate})`);
    }

    // Hàm mới: Tạm dừng console để upload có thể sử dụng port
    async pauseConsoleForUpload() {
        console.log('⏸ Pausing console for upload...');

        const wasPlaying = this.state.isPlaying;

        // Dừng read loop
        if (this._reader) {
            try {
                await this._reader.cancel();
            } catch (e) {
                console.warn('Reader cancel warning:', e);
            }
            try {
                this._reader.releaseLock();
            } catch (e) {
                console.warn('Reader release warning:', e);
            }
            this._reader = null;
        }

        // Giải phóng writer
        if (this._writer) {
            try {
                this._writer.releaseLock();
            } catch (e) {
                console.warn('Writer release warning:', e);
            }
            this._writer = null;
        }

        // ✅ CRITICAL: Đóng port hoàn toàn để Upload có thể mở lại
        if (this._port) {
            try {
                await this._port.close();
                console.log('✓ Port closed for upload');
            } catch (e) {
                console.warn('Port close warning:', e);
            }
        }

        this._isPortOpen = false;
        this._useWebSerial = false;

        // Lưu trạng thái Play để khôi phục sau
        this._wasPlayingBeforeUpload = wasPlaying;

        console.log('✓ Console fully paused, port closed and ready for upload');
    }

    // Hàm mới: Tiếp tục console sau khi upload xong
    async resumeConsoleAfterUpload() {
        console.log('▶ Resuming console after upload...');

        const sharedPort = getSharedSerialPort();
        if (!sharedPort) {
            console.warn('No shared port available after upload');
            return;
        }

        // Chờ upload cleanup hoàn toàn
        await new Promise(resolve => setTimeout(resolve, 1000));

        try {
            // ✅ Kiểm tra xem port có còn kết nối không
            if (!sharedPort.readable || !sharedPort.writable) {
                console.warn('⚠ Port no longer available, please reconnect device');
                await this._handleDeviceDisconnect();
                return;
            }

            // Mở lại port với baudrate từ console settings
            const baudRate = parseInt(this.props.baudrate, 10);
            await sharedPort.open({baudRate: baudRate});
            console.log(`✓ Port reopened at ${baudRate} baud for console`);

            // Khởi tạo lại writer
            this._port = sharedPort;
            this._writer = sharedPort.writable.getWriter();
            this._isPortOpen = true;
            this._useWebSerial = true;

            console.log('✓ Writer reinitialized');

            // Khởi tạo lại reader nếu trước đó đang Play
            if (this._wasPlayingBeforeUpload && !this._reader) {
                this._reader = sharedPort.readable.getReader();
                this.setState({isPlaying: true});
                this._startBackgroundReading();
                console.log('✓ Reader reinitialized, Play mode restored');
            }

            console.log('✓ Console resumed successfully');
        } catch (err) {
            console.error('Failed to resume console:', err);

            // ✅ Nếu không thể mở lại port, reset và yêu cầu chọn lại
            if (err.message && (err.message.includes('disconnected') || err.message.includes('not found'))) {
                console.warn('⚠ Device disconnected during upload, please reconnect');
                await this._handleDeviceDisconnect();
            } else {
                this._isPortOpen = false;
                this._useWebSerial = false;
            }
        }
    }

    // Hàm yêu cầu port USB native của Espressif (USB-OTG/USB-JTAG-Serial)
    async requestUsbPort() {
        if (!('serial' in navigator)) {
            console.error('Web Serial API not supported');
            return null;
        }

        try {
            // Filter cho Espressif USB devices
            const filters = [
                { usbVendorId: this.ESPRESSIF_VID }, // Espressif VID - bắt tất cả ESP32 USB native
            ];

            console.log('🔌 Requesting Espressif USB port...');
            console.log('📋 Supported devices: ESP32-S2, ESP32-S3, ESP32-C3, ESP32-C6 with native USB');
            
            const port = await navigator.serial.requestPort({ filters });
            const portInfo = port.getInfo();
            
            console.log(`✓ Selected USB port - VID: 0x${portInfo.usbVendorId?.toString(16)}, PID: 0x${portInfo.usbProductId?.toString(16)}`);
            
            // Xác định loại USB
            if (portInfo.usbProductId === this.USB_JTAG_SERIAL_PID) {
                console.log('📱 Detected: USB-JTAG-Serial (built-in JTAG)');
            } else {
                console.log('📱 Detected: USB-OTG/CDC (native USB)');
            }
            
            return port;
        } catch (err) {
            if (err.name === 'NotFoundError') {
                console.log('⚠ No Espressif USB device selected or found');
                console.log('💡 Make sure your ESP32-S3 is connected via USB port (not UART)');
            } else {
                console.error('Error requesting USB port:', err);
            }
            return null;
        }
    }

    async handleClickScan () {
        // Nếu đang flash thì không làm gì
        if (this.state.isFlashing) {
            console.log('⚠ Already flashing, please wait...');
            return;
        }

        // Mặc định sử dụng USB native
        const portType = 'usb';
        this.setState({ portType });
        
        console.log('🔌 Using USB Native port for flashing');

        let port = null;
        
        if (portType === 'usb') {
            // Sử dụng USB native port
            port = await this.requestUsbPort();
            if (!port) {
                console.error('Cannot flash: USB port not available');
                this.props.onNoPeripheralIsConnected();
                return;
            }
            
            // Kiểm tra xem có cần nhấn nút BOOT không
            const portInfo = port.getInfo();
            if (portInfo.usbProductId !== this.USB_JTAG_SERIAL_PID) {
                // USB-OTG có thể cần nhấn BOOT
                const bootConfirm = window.confirm(
                    '⚠️ ESP32-S3 USB-OTG Mode\n\n' +
                    'Nếu thiết bị không vào bootloader tự động, bạn cần:\n\n' +
                    '1. Nhấn giữ nút BOOT\n' +
                    '2. Nhấn và thả nút RESET\n' +
                    '3. Thả nút BOOT\n\n' +
                    'Nhấn [OK] khi đã sẵn sàng để tiếp tục flash.'
                );
                if (!bootConfirm) {
                    console.log('User cancelled flash operation');
                    return;
                }
            }
            
            this._port = port;
        } else {
            // Sử dụng COM port (cách cũ)
            const portOpened = await this.ensurePortOpen();
            if (!portOpened) {
                console.error('Cannot flash: COM port not available');
                return;
            }
            port = this._port;
        }

        // Bắt đầu quá trình flash bin files
        await this.flashBinFilesToESP32(portType);
    }

    async flashBinFilesToESP32 (portType = 'com') {
        try {
            this.setState({ isFlashing: true, flashProgress: 0 });
            console.log(`🚀 Starting flash bin files to ESP32-S3 via ${portType === 'usb' ? 'USB Native' : 'COM'}...`);

            // Dừng read loop nếu đang chạy
            if (this._reader) {
                try {
                    await this._reader.cancel();
                } catch (e) {
                    console.warn('Reader cancel warning:', e);
                }
                try {
                    this._reader.releaseLock();
                } catch (e) {
                    console.warn('Reader release warning:', e);
                }
                this._reader = null;
            }

            // Giải phóng writer
            if (this._writer) {
                try {
                    this._writer.releaseLock();
                } catch (e) {
                    console.warn('Writer release warning:', e);
                }
                this._writer = null;
            }

            // Đóng port để ESPLoader có thể sử dụng
            if (this._port) {
                try {
                    await this._port.close();
                    console.log('✓ Port closed for flashing');
                } catch (e) {
                    console.warn('Port close warning:', e);
                }
            }
            this._isPortOpen = false;

            // Chờ một chút để đảm bảo port được giải phóng
            await new Promise(resolve => setTimeout(resolve, 500));

            // Chuẩn bị file array với các địa chỉ flash
            const flashMap = {
                'bootloader.bin': { data: bootloaderBin, address: 0x0000 },
                'partitions.bin': { data: partitionsBin, address: 0x8000 },
                'firmware.bin': { data: firmwareBin, address: 0x10000 }
            };

            const fileArray = [];

            // Fetch và chuẩn bị các file bin
            for (const [fileName, config] of Object.entries(flashMap)) {
                console.log(`📦 Loading ${fileName}...`);

                try {
                    const response = await fetch(config.data);
                    const arrayBuffer = await response.arrayBuffer();
                    const uint8Array = new Uint8Array(arrayBuffer);
                    
                    // Chuyển đổi từng chunk để tránh stack overflow với file lớn
                    const CHUNK_SIZE = 8192;
                    let binaryData = '';
                    for (let i = 0; i < uint8Array.length; i += CHUNK_SIZE) {
                        const chunk = uint8Array.subarray(i, Math.min(i + CHUNK_SIZE, uint8Array.length));
                        binaryData += String.fromCharCode.apply(null, chunk);
                    }

                    fileArray.push({
                        data: binaryData,
                        address: config.address
                    });

                    console.log(`✓ ${fileName}: ${(binaryData.length / 1024).toFixed(2)} KB -> 0x${config.address.toString(16)}`);
                } catch (err) {
                    console.error(`❌ Failed to load ${fileName}:`, err);
                    throw new Error(`Failed to load ${fileName}: ${err.message}`);
                }
            }

            this.setState({ flashProgress: 10 });

            if (fileArray.length === 0) {
                throw new Error('Không tìm thấy file .bin nào để flash');
            }

            // Flash vào ESP32 với portType để xác định cách reset
            const flashResult = await this._flashToESP32(this._port, fileArray);

            console.log('✅ Flash hoàn tất!');
            this.setState({ isFlashing: false, flashProgress: 100 });

            // USB-OTG/USB-JTAG cần thời gian boot dài hơn vì USB stack phải khởi động
            const isUsbNative = flashResult && (flashResult.isUsbOtg || flashResult.isUsbJtagSerial || flashResult.isUsbNative);
            const bootWaitTime = isUsbNative ? 4000 : 1500; // USB native cần thời gian dài hơn
            
            console.log(`⏳ Waiting for ESP32 to boot... (${bootWaitTime}ms for ${isUsbNative ? 'USB-native' : 'UART'})`);
            await new Promise(resolve => setTimeout(resolve, bootWaitTime));

            // Mở lại port cho console và bắt đầu đọc JSON
            try {
                const baudRate = parseInt(this.props.baudrate, 10);
                
                // USB-OTG có thể cần thử lại nhiều lần vì port có thể chưa sẵn sàng
                let portOpened = false;
                const maxRetries = isUsbNative ? 5 : 3;
                
                for (let retry = 0; retry < maxRetries && !portOpened; retry++) {
                    try {
                        await this._port.open({ baudRate: baudRate });
                        portOpened = true;
                        console.log(`✓ Port reopened at ${baudRate} baud after flash`);
                    } catch (openErr) {
                        console.log(`⏳ Port not ready, retrying... (${retry + 1}/${maxRetries})`);
                        await new Promise(resolve => setTimeout(resolve, 1000));
                    }
                }
                
                if (!portOpened) {
                    throw new Error('Could not reopen port after flash');
                }
                
                this._writer = this._port.writable.getWriter();
                this._isPortOpen = true;
                this._useWebSerial = true;
                
                // Chờ thêm một chút để ESP32 ổn định và bắt đầu gửi dữ liệu
                await new Promise(resolve => setTimeout(resolve, isUsbNative ? 1000 : 500));

                // Bắt đầu đọc serial để nhận JSON response
                if (this._port && this._port.readable) {
                    this._reader = this._port.readable.getReader();
                    console.log('✓ Starting read loop to receive JSON after flash');

                    // Bắt đầu scan mode để đọc JSON
                    this.setState({
                        isScanning: true,
                        scannedDevices: [],
                        scanTimeLeft: 10
                    });
                    this._jsonBuffer = '';

                    // Countdown timer
                    this._scanCountdownInterval = setInterval(() => {
                        this.setState(prevState => {
                            const newTime = prevState.scanTimeLeft - 1;
                            if (newTime <= 0) {
                                clearInterval(this._scanCountdownInterval);
                                this._scanCountdownInterval = null;
                            }
                            return { scanTimeLeft: newTime };
                        });
                    }, 1000);

                    // Timeout sau 10 giây nếu không nhận được JSON
                    this._scanTimeout = setTimeout(() => {
                        if (this.state.isScanning) {
                            console.log('⏱ Scan timeout - no JSON found after 10 seconds');
                            this.setState({
                                isScanning: false,
                                scannedDevices: [],
                                scanTimeLeft: 10
                            });
                            this._jsonBuffer = '';
                            this._scanTimeout = null;

                            if (this._scanCountdownInterval) {
                                clearInterval(this._scanCountdownInterval);
                                this._scanCountdownInterval = null;
                            }
                        }
                    }, 10000);
                    this.writeToPeripheral('scan');
                    // Bắt đầu background reading
                    this._startBackgroundReading();
                    console.log('✓ Waiting for JSON response from ESP32 (10s timeout)');
                }
            } catch (err) {
                console.warn('Could not reopen port:', err);
            }

        } catch (error) {
            console.error('❌ Lỗi khi flash bin files:', error);
            this.setState({ isFlashing: false, flashProgress: 0 });

            // Reset port nếu có lỗi
            if (error.message && (error.message.includes('disconnected') || error.message.includes('not found'))) {
                await this._handleDeviceDisconnect();
            }
        }
    }

    async _flashToESP32(device, fileArray) {
        let deviceInfo = device.getInfo();
        let transport = new Transport(device, true);

        const terminal = {
            writeLine: (msg) => {
                console.log(msg);
                this.props.onAppendEsp32UploadText?.(msg + '\r\n');
            },
            write: (msg) => {
                console.log(msg);
                this.props.onAppendEsp32UploadText?.(msg);
            },
            clean: () => {},
            onLineReceived: null,
            rawWrite: (msg) => {},
            read: () => Promise.resolve('')
        };

        const flashOptions_setup = {
            transport,
            baudrate: 115200,
            terminal: terminal,
        };

        let esploader = new ESPLoader(flashOptions_setup);

        try {
            let chip = await esploader.main();

            let chipDescription = 'ESP32';
            if (typeof chip.getChipDescription === 'function') {
                chipDescription = chip.getChipDescription();
            } else if (chip.CHIP_DEFS && chip.CHIP_DEFS.CHIP_NAME) {
                chipDescription = chip.CHIP_DEFS.CHIP_NAME;
            } else if (chip.rom && chip.rom.CHIP_NAME) {
                chipDescription = chip.rom.CHIP_NAME;
            }

            console.log(`Connected to ${deviceInfo.usbProductId ? 'USB Serial Device' : 'Serial Device'} - Chip: ${chipDescription}`);

            await esploader.eraseFlash();
            console.log('Flash erased successfully.');

            const flashOptions = {
                fileArray: fileArray,
                eraseAll: false,
                compress: true,
                reportProgress: (fileIndex, written, total) => {
                    const percent = Math.round((written / total) * 100);
                    console.log(`📝 Flashing file ${fileIndex + 1}/${fileArray.length}: ${percent}%`);
                    // Tính tiến độ tổng: 30-90% dành cho flash
                    const overallProgress = 30 + (((fileIndex * 100) + percent) / fileArray.length) * 0.6;
                    this.setState({ flashProgress: Math.min(90, Math.round(overallProgress)) });
                },
                calculateMD5Hash: (image) => CryptoJS.MD5(CryptoJS.enc.Latin1.parse(image))
            };

            await esploader.writeFlash(flashOptions);
            await esploader.after();
            console.log('Flashing process completed.');
            console.log('🔄 Resetting ESP32...');
            try {
                // Thử gọi hardReset nếu có
                if (typeof esploader.hardReset === 'function') {
                    await esploader.hardReset();
                    console.log('✓ Hard reset via esploader');
                } else if (transport && typeof transport.setDTR === 'function') {
                    // Reset thủ công bằng DTR/RTS signals
                    await transport.setDTR(false);
                    await transport.setRTS(true);
                    await new Promise(resolve => setTimeout(resolve, 100));
                    await transport.setRTS(false);
                    await new Promise(resolve => setTimeout(resolve, 100));
                    console.log('✓ Hard reset via DTR/RTS');
                } else {
                    // Fallback: gọi after() để reset
                    await esploader.after();
                    console.log('✓ Reset via esploader.after()');
                }
            } catch (resetErr) {
                console.warn('Reset warning:', resetErr);
            }

            console.log('✓ ESP32 should restart now');

        } finally {
            // ✅ Cleanup transport và giải phóng port
            try {
                if (transport && transport.reader) {
                    try {
                        await transport.reader.cancel();
                    } catch (e) {
                        console.warn('Reader cancel failed:', e);
                    }
                    try {
                        transport.reader.releaseLock();
                    } catch (e) {
                        console.warn('Reader release failed:', e);
                    }
                }
                if (transport) {
                    await transport.disconnect();
                    console.log('Transport disconnected.');
                }

                // ✅ Đóng port sau khi flash xong để Console có thể mở lại
                if (device && device.readable) {
                    try {
                        await device.close();
                        console.log('✓ Port closed after flash, ready for console');
                    } catch (e) {
                        console.warn('Port close warning:', e);
                    }
                }

                // Chờ để đảm bảo port đã được giải phóng hoàn toàn
                await new Promise(resolve => setTimeout(resolve, 500));

            } catch (error) {
                console.error('Error during cleanup:', error);
            }

            deviceInfo = null;
            transport = null;
            esploader = null;

            console.log('✓ Flash cleanup completed, port available for console.');
        }
    }

    async _startBackgroundReading () {
        // Read loop chạy ngầm
        while (this._reader) {
            try {
                const {value, done} = await this._reader.read();
                if (done) break;
                if (value) {
                    const chunk = value instanceof Uint8Array ? value : new Uint8Array(value);
                    this.onWebSerialData(chunk);
                }
            } catch (readErr) {
                console.error('Read error:', readErr);

                // ✅ Nếu thiết bị bị ngắt kết nối, reset toàn bộ
                if (readErr.message && readErr.message.includes('disconnected')) {
                    console.warn('⚠ Device disconnected, resetting port...');
                    await this._handleDeviceDisconnect();
                }
                break;
            }
        }
    }

    // Hàm mới: Xử lý khi thiết bị bị ngắt kết nối
    async _handleDeviceDisconnect() {
        console.log('🔌 Handling device disconnect...');

        // Reset local state
        this._reader = null;
        this._writer = null;
        this._port = null;
        this._isPortOpen = false;
        this._useWebSerial = false;

        // Reset shared port
        setSharedSerialPort(null, null);

        // Reset UI state
        this.setState({
            isPlaying: false,
            isScanning: false,
            scanTimeLeft: 10
        });

        // Clear timers
        if (this._scanTimeout) {
            clearTimeout(this._scanTimeout);
            this._scanTimeout = null;
        }
        if (this._scanCountdownInterval) {
            clearInterval(this._scanCountdownInterval);
            this._scanCountdownInterval = null;
        }

        console.log('✓ Port reset complete. Please reconnect device and select port again.');
    }

    componentDidMount () {
        this.props.vm.addListener('PERIPHERAL_RECIVE_DATA', this.onReciveData);
        if (this.props.peripheralName) {
            this.props.vm.setPeripheralBaudrate(this.props.deviceId, parseInt(this.props.baudrate, 10));
        }

        // Lắng nghe sự kiện upload để tạm dừng console
        window.addEventListener('beforeUpload', this.pauseConsoleForUpload);
        window.addEventListener('afterUpload', this.resumeConsoleAfterUpload);
    }

    componentWillUnmount () {
        this.props.vm.removeListener('PERIPHERAL_RECIVE_DATA', this.onReciveData);
        try {
            this._stopWebSerial();
        } catch (e) {}

        // Cleanup scan timeout
        if (this._scanTimeout) {
            clearTimeout(this._scanTimeout);
            this._scanTimeout = null;
        }
        if (this._scanCountdownInterval) {
            clearInterval(this._scanCountdownInterval);
            this._scanCountdownInterval = null;
        }

        // Cleanup event listeners
        window.removeEventListener('beforeUpload', this.pauseConsoleForUpload);
        window.removeEventListener('afterUpload', this.resumeConsoleAfterUpload);
    }

    appendBuffer (arr1, arr2) {
        const arr = new Uint8Array(arr1.byteLength + arr2.byteLength);
        arr.set(arr1, 0);
        arr.set(arr2, arr1.byteLength);
        return arr;
    }

    onReciveData (data) {
        if (this._useWebSerial) return;

        if (this.props.isPause) {
            return;
        }

        if (this.state.isScanning) {
            const text = new TextDecoder('utf-8').decode(data);
            this._jsonBuffer += text;

            const startIdx = this._jsonBuffer.indexOf('{');
            const endIdx = this._jsonBuffer.lastIndexOf('}');

            if (startIdx !== -1 && endIdx !== -1 && startIdx < endIdx) {
                try {
                    const jsonStr = this._jsonBuffer.substring(startIdx, endIdx + 1);
                    const parsed = JSON.parse(jsonStr);

                    if (parsed.devices && Array.isArray(parsed.devices)) {
                        // Save to global store for blocks to use
                        setScannedDevices(parsed.devices);
                        console.log('✓ JSON parsed from onReciveData:', parsed.devices.length, 'devices');
                        this.setState({
                            scannedDevices: parsed.devices,
                            isScanning: false
                        });
                        this._jsonBuffer = '';
                        return;
                    }
                } catch (e) {
                    console.error('Failed to parse JSON:', e.message);
                }
            }
        }

        if (this._recivceBuffer.byteLength + data.byteLength >= MAX_CONSOLE_LENGTH) {
            this._recivceBuffer = this._recivceBuffer.slice(
                this._recivceBuffer.byteLength + data.byteLength - MAX_CONSOLE_LENGTH);
        }

        this._recivceBuffer = this.appendBuffer(this._recivceBuffer, data);

        if (!this._updateTimeoutID) {
            this._updateTimeoutID = setTimeout(() => {
                this.setState({
                    consoleArray: this._recivceBuffer
                });
                this._updateTimeoutID = null;
            }, 50);
        }
    }

    onWebSerialData (data) {
        if (this.props.isPause) return;

        if (this.state.isScanning) {
            const text = new TextDecoder('utf-8').decode(data);
            this._jsonBuffer += text;

            const startIdx = this._jsonBuffer.indexOf('{');
            const endIdx = this._jsonBuffer.lastIndexOf('}');

            if (startIdx !== -1 && endIdx !== -1 && startIdx < endIdx) {
                try {
                    const jsonStr = this._jsonBuffer.substring(startIdx, endIdx + 1);
                    const parsed = JSON.parse(jsonStr);

                    if (parsed.devices && Array.isArray(parsed.devices)) {
                        // Save to global store for blocks to use
                        setScannedDevices(parsed.devices);
                        console.log('✓ JSON parsed from onWebSerialData:', parsed.devices.length, 'devices');
                        this.setState({
                            scannedDevices: parsed.devices,
                            isScanning: false
                        });
                        this._jsonBuffer = '';
                        return;
                    }
                } catch (e) {
                    console.error('Failed to parse JSON:', e.message);
                }
            }
        }

        if (this._recivceBuffer.byteLength + data.byteLength >= MAX_CONSOLE_LENGTH) {
            this._recivceBuffer = this._recivceBuffer.slice(
                this._recivceBuffer.byteLength + data.byteLength - MAX_CONSOLE_LENGTH);
        }

        this._recivceBuffer = this.appendBuffer(this._recivceBuffer, data);

        if (!this._updateTimeoutID) {
            this._updateTimeoutID = setTimeout(() => {
                this.setState({
                    consoleArray: this._recivceBuffer
                });
                this._updateTimeoutID = null;
            }, 50);
        }
    }

    handleClickClean () {
        this._recivceBuffer = new Uint8Array(0);
        this.setState({
            consoleArray: new Uint8Array(0)
        });
    }

    handleClickPause () {
        this.props.onSwitchPause();
    }

    handleKeyPress (e) {
        const keyCode = e.keyCode || e.which || e.charCode;

        if (keyCode === 13) {
            this.handleClickSend();
        }
    }

    handleKeyDown (e) {
        const keyCode = e.keyCode || e.which || e.charCode;
        const ctrlKey = e.ctrlKey || e.metaKey;

        if (keyCode === 65 && ctrlKey) {
            e.preventDefault();
            this.writeToPeripheral(String.fromCharCode(1));
        }
        if (keyCode === 66 && ctrlKey) {
            e.preventDefault();
            this.writeToPeripheral(String.fromCharCode(2));
        }
        if (keyCode === 67 && ctrlKey) {
            e.preventDefault();
            this.writeToPeripheral(String.fromCharCode(3));
        }
        if (keyCode === 68 && ctrlKey) {
            e.preventDefault();
            this.writeToPeripheral(String.fromCharCode(4));
        }
    }

    handleInputChange (e) {
        this.setState({
            dataToSend: e.target.value
        });
    }

    async writeToPeripheral (data) { 
        // Trường hợp 1: Đang dùng USB native nhưng writer chưa sẵn sàng
        if (this.state.portType === 'usb' && this._port) {
            try {
                // Thử khởi tạo lại writer nếu port còn hợp lệ
                if (this._port.writable && !this._writer) {
                    this._writer = this._port.writable.getWriter();
                    this._useWebSerial = true;
                    console.log('✓ Writer reinitialized for USB port');
                }
                
                if (this._writer) {
                    const encoder = new TextEncoder();
                    await this._writer.write(encoder.encode(data));
                    console.log('✓ Data sent via USB native port');
                    return;
                }
            } catch (err) {
                console.error('Failed to write to USB port:', err);
                
                // USB port có thể cần mở lại
                if (!this._port.writable) {
                    console.warn('⚠ USB port not writable, may need to reconnect');
                }
            }
        }

        // Trường hợp 2: Đang sử dụng WebSerial (cả USB native và COM)
        if (this._useWebSerial && this._writer) {
            try {
                const encoder = new TextEncoder();
                await this._writer.write(encoder.encode(data));
                console.log(`✓ Data sent via WebSerial (${this.state.portType || 'unknown'} port)`);
                return;
            } catch (err) {
                console.error('Failed to write to serial port:', err);
                
                // Nếu lỗi do disconnect, reset port
                if (err.message && err.message.includes('disconnected')) {
                    await this._handleDeviceDisconnect();
                }
            }
        }
        
        // Trường hợp 3: Đang dùng USB native nhưng port chưa mở
        if (this.state.portType === 'usb' && !this._port) {
            console.warn('⚠ USB port not available. Please flash firmware first or reconnect device.');
            this.props.onNoPeripheralIsConnected();
            return;
        }
        
        // Trường hợp 4: Fallback - sử dụng VM peripheral (Bluetooth/Link cũ)
        if (this.props.peripheralName) {
            this.props.vm.writeToPeripheral(this.props.deviceId, data);
        } else {
            this.props.onNoPeripheralIsConnected();
        }
    }

    handleClickSend () {
        let data = this.state.dataToSend;
        if (this.props.eol === 'lf') {
            data = `${data}\n`;
        } else if (this.props.eol === 'cr'){
            data = `${data}\r`;
        } else if (this.props.eol === 'lfAndCr'){
            data = `${data}\r\n`;
        }
        this.writeToPeripheral(data);
    }

    handleSelectBaudrate (e) {
        if (this.props.peripheralName) {
            const index = e.target.selectedIndex;
            this.props.onSetBaudrate(baudrateList[index].key);
            this.props.vm.setPeripheralBaudrate(this.props.deviceId, baudrateList[index].value);
        } else {
            this.props.onNoPeripheralIsConnected();
        }
    }

    handleSelectEol (e) {
        const index = e.target.selectedIndex;
        this.props.onSetEol(eolList[index].key);
    }

    async handleClickPlay () {
        if (this.state.isPlaying) {
            await this._stopReadLoop();
        } else {
            await this._startReadLoop();
        }
    }

    async _startReadLoop () {
        // Tái sử dụng port đã mở hoặc mở port mới
        const portReady = await this.ensurePortOpen();
        if (!portReady) {
            return;
        }

        // ✅ Bắt đầu read loop nếu chưa có
        if (!this._reader) {
            // Kiểm tra port còn hợp lệ
            if (!this._port || !this._port.readable) {
                console.error('⚠ Port not available for reading');
                await this._handleDeviceDisconnect();
                return;
            }

            this._reader = this._port.readable.getReader();
            console.log('✓ Serial console started reading');

            // Bắt đầu read loop
            this._startBackgroundReading();
        } else {
            console.log('✓ Read loop already running');
        }

        this.setState({isPlaying: true});
    }

    async _stopReadLoop () {
        this.setState({isPlaying: false});

        if (this._reader) {
            try {
                await this._reader.cancel();
            } catch (e) {}
            try {
                this._reader.releaseLock();
            } catch (e) {}
            this._reader = null;
            console.log('✓ Read loop stopped');
        }
    }

    async _stopWebSerial () {
        this._useWebSerial = false;
        this._isPortOpen = false;

        try {
            if (this._writer) {
                try {
                    this._writer.releaseLock();
                } catch (e) {}
                this._writer = null;
            }
            if (this._reader) {
                try {
                    await this._reader.cancel();
                } catch (e) {}
                try {
                    this._reader.releaseLock();
                } catch (e) {}
                this._reader = null;
            }

            if (this._port) {
                try {
                    await this._port.close();
                    console.log('✓ Port closed');
                } catch (e) {
                    console.warn('Port close warning:', e);
                }
                this._port = null;
            }
        } catch (e) {
            console.error('Stop web serial error:', e);
        }

        this.setState({isPlaying: false});
    }

    handleClickHexForm () {
        this.props.onSwitchHexForm();
    }

    handleClickAutoScroll () {
        this.props.onSwitchAutoScroll();
    }

    render () {
        const {
            ...props
        } = this.props;
        return (
            <HardwareConsoleComponent
                baudrate={this.props.baudrate}
                baudrateList={baudrateList}
                consoleArray={this.state.consoleArray}
                deviceId={this.props.deviceId}
                eol={this.props.eol}
                eolList={eolList}
                isAutoScroll={this.props.isAutoScroll}
                isHexForm={this.props.isHexForm}
                isPause={this.props.isPause}
                isPlaying={this.state.isPlaying}
                isRtl={this.props.isRtl}
                onClickPlay={this.handleClickPlay}
                onClickClean={this.handleClickClean}
                onClickPause={this.handleClickPause}
                onClickAutoScroll={this.handleClickAutoScroll}
                onClickHexForm={this.handleClickHexForm}
                onClickSend={this.handleClickSend}
                onClickSerialportMenu={this.props.handleClickSerialportMenu}
                onKeyPress={this.handleKeyPress}
                onKeyDown={this.handleKeyDown}
                onInputChange={this.handleInputChange}
                onRequestSerialportMenu={this.props.handleRequestSerialportMenu}
                onSelectBaudrate={this.handleSelectBaudrate}
                onSelectEol={this.handleSelectEol}
                serialportMenuOpen={serialportMenuOpen}
                {...props}
                isScanning={this.state.isScanning}
                scannedDevices={this.state.scannedDevices}
                scanTimeLeft={this.state.scanTimeLeft}
                onClickScan={this.handleClickScan}
                isFlashing={this.state.isFlashing}
                flashProgress={this.state.flashProgress}
            />
        );
    }
}

HardwareConsole.propTypes = {
    baudrate: PropTypes.string.isRequired,
    deviceId: PropTypes.string.isRequired,
    eol: PropTypes.string.isRequired,
    handleClickSerialportMenu: PropTypes.func.isRequired,
    handleRequestSerialportMenu: PropTypes.func.isRequired,
    isAutoScroll: PropTypes.bool.isRequired,
    isHexForm: PropTypes.bool.isRequired,
    isPause: PropTypes.bool.isRequired,
    intl: intlShape.isRequired,
    isRtl: PropTypes.bool,
    onNoPeripheralIsConnected: PropTypes.func.isRequired,
    onSetBaudrate: PropTypes.func.isRequired,
    onSetEol: PropTypes.func.isRequired,
    onSwitchAutoScroll: PropTypes.func.isRequired,
    onSwitchHexForm: PropTypes.func.isRequired,
    onSwitchPause: PropTypes.func.isRequired,
    peripheralName: PropTypes.string,
    vm: PropTypes.instanceOf(VM).isRequired
};

const mapStateToProps = state => ({
    baudrate: state.scratchGui.hardwareConsole.baudrate,
    deviceId: state.scratchGui.device.deviceId,
    eol: state.scratchGui.hardwareConsole.eol,
    isAutoScroll: state.scratchGui.hardwareConsole.isAutoScroll,
    isHexForm: state.scratchGui.hardwareConsole.isHexForm,
    isPause: state.scratchGui.hardwareConsole.isPause,
    isRtl: state.locales.isRtl,
    peripheralName: state.scratchGui.connectionModal.peripheralName,
    serialportMenuOpen: serialportMenuOpen(state)
});

const mapDispatchToProps = dispatch => ({
    handleClickSerialportMenu: () => dispatch(openSerialportMenu()),
    handleRequestSerialportMenu: () => dispatch(closeSerialportMenu()),
    onNoPeripheralIsConnected: () => showAlertWithTimeout(dispatch, 'connectAPeripheralFirst'),
    onSetBaudrate: baudrate => dispatch(setBaudrate(baudrate)),
    onSetEol: eol => dispatch(setEol(eol)),
    onSwitchAutoScroll: () => dispatch(switchAutoScroll()),
    onSwitchHexForm: () => dispatch(switchHexForm()),
    onSwitchPause: () => dispatch(switchPause())
});

export default compose(
    injectIntl,
    connect(
        mapStateToProps,
        mapDispatchToProps
    )
)(HardwareConsole);
